"use strict";

const { getTreeviewSubSectionItemById, getItemById } = require("./helpers");
const QBreadcrumbMenu = require("./q-breadcrumb-menu");
const QDashboardMenu = require("./q-dashboard-menu");
const QMegaMenu = require("./q-mega-menu");
const QPortfolioMenu = require("./q-portfolio-menu");
const { signal } = require('@node_modules/@preact/signals-core');

class QMenu {
    constructor (config) {
        this._templates = config.templates || {};
        this._menuConfig = this._includeUUIDConfig(config.menu);
        this._userName= config.userName;
        this._homeRoute= config.homeRoute;
        this._lazy = config.lazyLoad;
        this._useTitleConfig = config.useTitleConfig;
        this._productName = config.productName;
        this._saveScrollPosition = config.saveScrollPosition;
        this._saveCategoryActive = config.saveCategoryActive;
        this._savedActiveItemPath = this._getActiveDashboardItemPath();

        this._setParentConfig();
        this._createSignals();
        
        const hasActiveItem = this._setAsIsActive();
        
        this._setIsCompany(hasActiveItem);
        this._setPath();

        this._portfolioMenuObject = new QPortfolioMenu('q_portfolio_menu', {
            menuConfig: this._menuConfig,
        });

        this._megaMenuObject = new QMegaMenu(
            'q_mega_menu',
            {
                menuConfig: this._menuConfig,
                template: this._templates.megaMenu,
                lazyLoad: this._lazy,
                useTitle: this._useTitleConfig,
                productName: this._productName,
                homeRoute: this._homeRoute,
                saveScrollPosition: this._saveScrollPosition
            },
            {
                treeview: (config.dependencies || {}).treeview
            }
        );

        this._breadcrumbMenuObject = new QBreadcrumbMenu('q_breadcrumb_menu', {
            menuConfig: this._menuConfig,
            template: this._templates.breadcrumb,
            userName: this._userName,
            homeRoute: this._homeRoute,
            lazyLoad: this._lazy,
            useTitle: this._useTitleConfig,
            ignoreActiveItem: Boolean(this._savedActiveItemPath),
        });

        this._dashboardMenuObject = new QDashboardMenu('q_dashboard_menu', {
            menuConfig: this._menuConfig,
            template: this._templates.dashboard,
            lazyLoad: this._lazy,
            saveCategoryActive: this._saveCategoryActive
        });
    }

    _createSignals() {
        const createItemSignals = (item) => {
            item.isVisible = signal(true);
            item._isFavorite = signal(item.isFavorite);
            item._isRecent = signal(item.isRecent);
        };

        const createSignal = (item) => {
            if (item.withTreeview) {
                item.isVisible = signal(true);
             
                createSignalTreeview(item.items);
            }
            else if (item.items) {
                item.isVisible = signal(true);

                item.items.forEach(createSignal);
            }
            else createItemSignals(item);
        };

        const createSignalTreeview = (item) => {
            if (!item.menuItems && !item.items) createItemSignals(item);
            if (item.menuItems) item.menuItems.forEach(createSignalTreeview);
            if (item.items) item.items.forEach(createSignalTreeview);
        };

        this._menuConfig.sections.forEach(createSignal);
    }

    _includeUUIDConfig(menuConfig) {
        const getId = () => uuidv4().replace(/-/g, '_');
        const makeId = (item) => {
            item._uuid = getId();

            if (item.withTreeview) makeIdTreeview(item.items);
            else if (item.items) item.items.forEach(makeId);
        };

        const makeIdTreeview = (item) => {
            item._uuid = getId();
            
            if (item.menuItems) item.menuItems.forEach(makeIdTreeview);
            if (item.items) item.items.forEach(makeIdTreeview);
        }

        menuConfig.sections.forEach(makeId);

        return menuConfig;
    }

    _getActiveDashboardItemPath() {
        const path = localStorage.getItem('BREADCRUMB_SUBSECTION_FILTER');

        localStorage.removeItem('BREADCRUMB_SUBSECTION_FILTER');

        return path;
    }

    _setParentConfig() {
        const setParent = (current, parent) => {
            current._parent = parent;

            if (current.withTreeview) {
                setTreeviewParent(current.items, current);
            }
            else if (current.items) current.items.forEach((item) => setParent(item, current));
        }

        const setTreeviewParent = (current, parent) => {
            current._parent = parent;

            if (current.menuItems) current.menuItems.forEach((menuItem) => setTreeviewParent(menuItem, current));
            if (current.items) current.items.forEach((subItem) => setTreeviewParent(subItem, current));
        }

        this._menuConfig.sections.forEach((section) => setParent(section, null));
    }

    _setAsIsActive(){
        let hasActiveItem = false;

        this._menuConfig.sections.forEach((el)=>{
            el.isActive = this._setAsActiveItems(el, null);
            hasActiveItem = hasActiveItem || el.isActive;
        });

        return hasActiveItem;
    }

    _setAsActiveItems(item, pathCaptions) {
        if (!item.items) {
            return item.isActive = item.isActive || pathCaptions == this._savedActiveItemPath;
        }
        if (item.withTreeview) {
            return item.isActive = `${pathCaptions}__${item.caption}` == this._savedActiveItemPath || this._setAsActiveTreeviewItem(item.items);
        }

        let anyItemActive = false;
        let currentTitle = '';
        
        for (const el of item.items) {
            if (el.title) currentTitle = el.title;

            const currentPathCaption = pathCaptions ? `${pathCaptions}__${currentTitle ? (currentTitle + '__') : ''}${item.caption}` : item.caption + (currentTitle ? '__' + currentTitle : '');

            if (anyItemActive = this._savedActiveItemPath == currentPathCaption + '__' + el.caption) {
                el.isActive = true;

                break;
            }

            if (anyItemActive = this._setAsActiveItems(el, currentPathCaption)) {
                break;
            }
        }

        item.isActive = anyItemActive;

        return anyItemActive;
    }

    _setAsActiveTreeviewItem(item) {
        let anyItemActive = false;

        item.menuItems.find((menuItem) => anyItemActive = menuItem.isActive);

        if (anyItemActive) return anyItemActive;

        item.items.find((subItem) => anyItemActive = this._setAsActiveTreeviewItem(subItem));

        item.isActive = anyItemActive;

        return anyItemActive;
    }

    _setIsCompany(hasActiveItem) {
        const setIsCompany = (item) => {
            if (!item.items) return item.isCompany;
            if (item.withTreeview) return false;

            let isCompany = false;

            item.items.find((el) => {
                isCompany = setIsCompany(el);

                if (isCompany && !hasActiveItem) {
                    el.isActive = true;
                }
            });

            return item.isCompany = isCompany;
        }

        this._menuConfig.sections.forEach((el) => {
            el.isCompany = setIsCompany(el)

            if (el.isCompany && !hasActiveItem) {
                el.isActive = true;
            }
        });
    }

    _setPath() {
        const setPath = (item, path) => {
            if(!item.items) return item.path = path;
            if (item.withTreeview) return setPathTreeview(item.items, path);
            
            item.items.forEach((subItem) => setPath(subItem, `${path ? `${path} / ` : ''}${item.caption}`));
        }

        const setPathTreeview = (item, path) => {
            if (!item.menuItems && !item.items) return item.path = path;
            if (item.menuItems) item.menuItems.forEach((menuItem) => setPathTreeview(menuItem, `${path ? `${path} / ` : ''}${item.caption}`))
            if (item.items) item.items.forEach((subItem) => setPathTreeview(subItem, `${path ? `${path} / ` : ''}${item.caption}`))
        }

        this._menuConfig.sections.forEach((section) => setPath(section, ''));
    }

    on(type, func) {
        switch(type) {
            case 'favorite-click':
                this._dashboardMenuObject.on(type, func);
                this._megaMenuObject.on(type, func);
                this._breadcrumbMenuObject.on(type, func);
                break;
        }
    }

    off(type, func) {
        switch(type) {
            case 'favorite-click':
                this._dashboardMenuObject.off(type, func);
                this._megaMenuObject.off(type, func);
                this._breadcrumbMenuObject.off(type, func);
                break;
        }
    }

    setFavorite(uuid, isFavorite) {
        const item = getItemById(this._menuConfig.sections, uuid);

        if (item) {
            item._isFavorite.value = isFavorite;
        }
    }

    clearScrollPosition() {
        this._megaMenuObject.resetScrollPosition();
    }

    clearCategoryActive() {
        this._dashboardMenuObject.clearCategoryActivePath();
    }

    getActiveItem() {
        let activeItem;

        const getActive = (item) => {
            if (item.withTreeview) return getActiveItemTreeview(item.items);
            if (item.items) return item.items.find(getActive);
            if (item.isActive) return activeItem = item;
        }

        const getActiveItemTreeview = (item) => {
            if (item.menuItems) item.menuItems.find((menuItem) => menuItem.isActive ? (activeItem = menuItem): false);
            if (activeItem) return activeItem;
            if (item.items) return item.items.find(getActiveItemTreeview);
        }

        this._menuConfig.sections
            .filter((section => !section.isQuickAccess))
            .find(getActive);

        return activeItem;
    }
}

// Webpack support
if (typeof module !== 'undefined') {
	module.exports = QMenu;
}
